import { cn } from "@/lib/utils";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import clsx from "clsx";
import { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { APIStatus } from "../../../../../api/APIStatus.type";
import getFormation from "../../../../../functions/getFormation";
import {
  PlayerRole,
  playerRoleLabelSelector,
} from "../../../../../redux/players/playersSlice";
import {
  BASIC_CHEMSTYLE,
  PlayerFilter,
  PlayerToInclude,
  RightPanelMode,
  Squad,
  SquadBuilderMode,
  SquadBuilderPlayerType,
} from "../../../../../redux/squadBuilder/squadBuilderUtils";
import Draggable from "../../../../molecules/Draggable/Draggable";
import Droppable from "../../../../molecules/Droppable/Droppable";
import { SquadBuilderPlayer } from "../../../../molecules/Player/SquadBuilderPlayer/SquadBuilderPlayer";
import Bench from "../Bench/Bench";
import { PitchItem } from "../PitchItem/PitchItem";

export type ClickPlayerType = ({
  name,
  resourceId,
  playerRoleId,
  chemistry,
  eaPlayerId,
  evoPath,
  evoPathId,
  chemstyleId,
  canEvolve,
}: {
  name: string;
  resourceId: number;
  playerRoleId: number;
  chemistry: number;
  eaPlayerId?: number;
  evoPath?: number[];
  evoPathId?: number;
  chemstyleId?: number;
  canEvolve?: boolean;
}) => void;

type Props = {
  squad: Squad;
  tempSquad: Squad;
  benchItems: SquadBuilderPlayerType[];
  tempBenchItems: SquadBuilderPlayerType[];
  playersToInclude?: PlayerToInclude[];
  statusBuildSquad: APIStatus;
  mode: SquadBuilderMode;
  handleChangePlayerRole: (index: number) => void;
  handleRemovePlayerOrRole: (index: number) => void;
  handleAddPlayer: (index: number) => void;
  handleAddPlayerFilters: (index: number) => void;
  handleMovePlayer: (fromIndex: number, toIndex: number) => void;
  handleOnClickPlayer: ClickPlayerType;
  handleOnClickPlayerMobile: (index: number) => void;
  playerRoles: PlayerRole[];
  handleAddEvoPlayer?: (index: number) => void;
  handleChangeChemstyle?: (index: number) => void;
  changingPitchItemOnIndex?: number;
  rightPanelMode?: RightPanelMode;
  playerFilters: PlayerFilter[];
  handleClearPlayerFilters?: (index: number) => void;
  tabletOrLargerMode?: boolean;
  desktopMode?: boolean;
  disablePlayerInteractions?: boolean;
  formation: string;
  tempFormation: string;
};

interface DrawPlayerProps {
  item: any;
  squad: Squad;
  tempSquad: Squad;
  mode: SquadBuilderMode;
  formationObject: any;
  changingPitchItemOnIndex?: number;
  rightPanelMode?: RightPanelMode;
  playerRoles: PlayerRole[];
  handleChangePlayerRole: (index: number) => void;
  handleRemovePlayerOrRole: (index: number) => void;
  handleAddPlayer: (index: number) => void;
  handleAddPlayerFilters: (index: number) => void;
  handleChangeChemstyle?: (index: number) => void;
  handleAddEvoPlayer?: (index: number) => void;
  handleOnClickPlayer: ClickPlayerType;
  handleOnClickPlayerMobile: (index: number) => void;
  tabletOrLargerMode?: boolean;
  desktopMode?: boolean;
  isDragging?: boolean;
}

const PlayerOnPitch: React.FC<DrawPlayerProps> = memo(
  ({
    item,
    squad,
    tempSquad,
    mode,
    formationObject,
    changingPitchItemOnIndex,
    rightPanelMode,
    handleChangePlayerRole,
    handleRemovePlayerOrRole,
    handleAddPlayer,
    handleAddPlayerFilters,
    handleChangeChemstyle,
    handleAddEvoPlayer,
    handleOnClickPlayer,
    handleOnClickPlayerMobile,
    tabletOrLargerMode,
    desktopMode,
    isDragging,
  }) => {
    const { player, chemIndex, numberOfFilters } = item;
    const tempChemistry = tempSquad.chemistry;
    const chemistry = squad.chemistry;

    const currentChemistry =
      tempChemistry?.playersChemistry[chemIndex] ??
      chemistry.playersChemistry[chemIndex] ??
      0;
    const previousChemistry = chemistry.playersChemistry[chemIndex] ?? 0;
    const isOutOfPosition =
      tempChemistry?.playersOutOfPosition.some(
        (p) => p.positionIndex === chemIndex,
      ) ||
      chemistry.playersOutOfPosition.some((p) => p.positionIndex === chemIndex);

    const metaRating = !isOutOfPosition
      ? player?.metaRatings?.[
          tempChemistry.playersChemistry[chemIndex] >= 0
            ? tempChemistry.playersChemistry[chemIndex]
            : chemistry.playersChemistry[chemIndex]
        ]?.metaRating
      : 0;

    const chemStyleId: number = !isOutOfPosition
      ? player.chemstyleId ||
        player.metaRatings?.[
          tempChemistry.playersChemistry[chemIndex] >= 0
            ? tempChemistry.playersChemistry[chemIndex]
            : chemistry.playersChemistry[chemIndex]
        ]?.chemstyleId
      : BASIC_CHEMSTYLE;

    return (
      <div
        id={"" + player.resourceId}
        key={player.resourceId}
        className={clsx(
          "relative",
          item.isTempPlayer && "opacity-75",
          rightPanelMode === "squad-overview" && "pointer-events-none",
        )}
      >
        <SquadBuilderPlayer
          onClickPlayerRole={() => handleChangePlayerRole(chemIndex)}
          handleAddPlayer={() => handleAddPlayer(chemIndex)}
          handleRemovePlayer={() => handleRemovePlayerOrRole(chemIndex)}
          handleChangeChemstyle={() => handleChangeChemstyle?.(chemIndex)}
          handleAddEvoPlayer={() => handleAddEvoPlayer?.(chemIndex)}
          handleAddPlayerFilters={() => handleAddPlayerFilters(chemIndex)}
          showPlayerFilters={numberOfFilters > 0}
          isSelected={
            changingPitchItemOnIndex === chemIndex && !item.isTempPlayer
          }
          onClickPlayer={(e) => {
            if (desktopMode) {
              handleOnClickPlayer({
                name: player.cardName,
                resourceId: player.resourceId,
                playerRoleId: player.playerRoleId,
                chemistry: currentChemistry,
                eaPlayerId: player.eaPlayerId,
                evoPath: player.evolutionPath,
                evoPathId: player.evoPathId,
                chemstyleId: chemStyleId,
                canEvolve: player.canEvolve,
              });
            } else {
              handleOnClickPlayerMobile(chemIndex);
              e.stopPropagation();
            }
          }}
          player={player}
          playerRoleId={player.playerRoleId}
          mode={mode}
          isOnPitch={true}
          chemistry={{
            currentChemistry,
            previousChemistry,
            isOutOfPosition,
          }}
          formationPosition={formationObject?.labels[chemIndex]}
          position={
            player?.possiblePositions?.length > 0
              ? !player?.possiblePositions?.includes(
                  formationObject?.labels[chemIndex],
                )
                ? player?.preferredPosition
                : formationObject?.labels[chemIndex]
              : ""
          }
          chemStyleId={chemStyleId}
          metaRating={metaRating}
          metaRatingZeroChem={player.metaRatings[0]?.metaRating}
          tabletOrLargerMode={tabletOrLargerMode}
          desktopMode={desktopMode}
          isDragging={isDragging}
        />
      </div>
    );
  },
);

const DraggingPlayerOnBench: React.FC<{
  item: SquadBuilderPlayerType;
  mode: SquadBuilderMode;
  playerRoles: PlayerRole[];
  tabletOrLargerMode?: boolean;
  isDragging?: boolean;
}> = memo(({ item, mode, tabletOrLargerMode, isDragging }) => {
  return (
    <SquadBuilderPlayer
      player={item}
      mode={mode}
      position={item.preferredPosition}
      metaRating={item.metaRating}
      metaRatingZeroChem={item.metaRating}
      chemStyleId={0}
      playerRoleId={item.playerRoleId}
      isOnPitch={!tabletOrLargerMode}
      isOnBench={tabletOrLargerMode}
      tabletOrLargerMode={tabletOrLargerMode}
      isDragging={isDragging}
    />
  );
});

interface DrawEmptyPositionProps {
  item: any;
  isOverItem?: number;
  formationObject: any;
  handleAddPlayer: (index: number) => void;
  handleAddPlayerFilters: (index: number) => void;
  handleChangePlayerRole: (index: number) => void;
  handleClearPlayerFilters?: (index: number) => void;
  changingPitchItemOnIndex?: number;
  rightPanelMode?: RightPanelMode;
  tabletOrLargerMode?: boolean;
  playerRoles?: PlayerRole[];
}

const DrawEmptyPosition: React.FC<DrawEmptyPositionProps> = memo(
  ({
    item,
    isOverItem,
    formationObject,
    handleAddPlayer,
    handleAddPlayerFilters,
    handleChangePlayerRole,
    handleClearPlayerFilters,
    changingPitchItemOnIndex,
    rightPanelMode,
    tabletOrLargerMode,
  }) => {
    const { chemIndex, x, y, isTempPlayer, numberOfFilters, playerRoleId } =
      item;
    const formationPosition = formationObject?.labels[chemIndex];
    const playerRoleLabel = useSelector(
      playerRoleLabelSelector({
        playerRoleId,
        appendPosition: false,
      }),
    );
    return (
      <div
        key={chemIndex}
        className={cn(isTempPlayer && "opacity-60")}
        style={{
          left: `${x}%`,
          top: `${y - 4}%`,
          transform: "translate(-50%, -50%)",
          position: "absolute",
        }}
      >
        <Droppable id={chemIndex}>
          <PitchItem
            isOverItem={isOverItem === chemIndex}
            onClickPlayerPlaceHolder={() => handleAddPlayer(chemIndex)}
            onClickPlayerRole={() => handleChangePlayerRole(chemIndex)}
            onClickFilter={() => handleAddPlayerFilters(chemIndex)}
            role={playerRoleLabel}
            activePlayerRole={
              changingPitchItemOnIndex === chemIndex &&
              rightPanelMode === "add-player-role"
            }
            activePlayerPlaceHolder={
              changingPitchItemOnIndex === chemIndex &&
              rightPanelMode === "add-player"
            }
            formationPosition={formationPosition}
            numberOfFilters={numberOfFilters}
            onClearFilters={() => handleClearPlayerFilters?.(chemIndex)}
            tabletOrLargerMode={tabletOrLargerMode}
          />
        </Droppable>
      </div>
    );
  },
);

export const PitchLayout = ({
  mode,
  squad,
  benchItems,
  tempBenchItems,
  tempSquad,
  handleChangePlayerRole,
  handleRemovePlayerOrRole,
  handleAddPlayer,
  handleAddPlayerFilters,
  handleMovePlayer,
  handleOnClickPlayer,
  handleOnClickPlayerMobile,
  handleChangeChemstyle,
  handleAddEvoPlayer,
  playerRoles,
  changingPitchItemOnIndex,
  rightPanelMode,
  playerFilters,
  handleClearPlayerFilters,
  tabletOrLargerMode,
  desktopMode,
  disablePlayerInteractions,
  formation,
  tempFormation,
}: Props) => {
  const { players } = squad;
  const { players: tempPlayers } = tempSquad;
  const formationObject = getFormation(tempFormation || formation);
  type PitchItem = {
    x: number;
    y: number;
    w: number;
    h: number;
    player: SquadBuilderPlayerType;
    chemIndex: number;
    tempPlayer: boolean;
    numberOfFilters: number;
    playerRoleId: number;
  };
  const [pitchItems, setPitchItems] = useState<PitchItem[]>(undefined);
  const [activeId, setActiveId] = useState<string>(undefined);
  const [activeData, setActiveData] = useState<{
    left: string;
    top: string;
    height: number;
    width: number;
  } | null>(undefined);
  const [isOverItem, setIsOverItem] = useState<string>(undefined);

  useEffect(() => {
    const tempPitchData: PitchItem[] = [];
    for (let i = 0; i < 11; i++) {
      let player = {} as SquadBuilderPlayerType;
      if (tempPlayers[i]?.playerRoleId || tempPlayers[i]?.resourceId) {
        player = { ...tempPlayers[i] }; // Clone the object to ensure mutability
      } else if (players[i]?.playerRoleId || players[i]?.resourceId) {
        player = { ...players[i] }; // Clone the object to ensure mutability
      }

      const isTempPlayer =
        !!tempPlayers[i].playerRoleId && tempPlayers[i]?.isAIPlayer;

      const chemIndex = player?.chemIndex || i;

      const numberOfFilters =
        playerFilters[chemIndex]?.filter((pf) => {
          return pf.type === "RANGE_FILTER"
            ? pf.maxValue || pf.minValue
            : pf.selectedOptions.length > 0;
        })?.length ?? 0;

      tempPitchData.push({
        x: formationObject.coordinates[chemIndex].x * 100,
        y: formationObject.coordinates[chemIndex].y * 100,
        w: desktopMode ? 174 : 80,
        h: desktopMode ? 222 : 110,
        player,
        chemIndex,
        tempPlayer: isTempPlayer,
        numberOfFilters,
        playerRoleId: player.playerRoleId,
      });
    }
    setPitchItems(tempPitchData);
  }, [
    players,
    tempPlayers,
    playerFilters,
    desktopMode,
    formationObject.coordinates,
  ]);

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
    setActiveData(event.active.data);
  };

  const handleDragEnd = (event: any) => {
    setActiveId(null);
    setIsOverItem(null);
    if (event.over?.id >= 0) {
      handleMovePlayer(+event.active?.id, +event.over?.id);
    }
  };

  const handleDragOver = (event: any) => {
    setIsOverItem(event.over?.id);
  };

  return (
    <>
      <DndContext
        collisionDetection={pointerWithin}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        sensors={useSensors(
          useSensor(PointerSensor, {
            activationConstraint: {
              distance: 2,
            },
          }),
          useSensor(TouchSensor),
        )}
        modifiers={[restrictToWindowEdges]}
      >
        <div className="desktop:w-[950px] sb-v2-md:min-w-[1100px] touch-device:w-[1140px] w-screen desktop:h-[800px] touch-device:relative mt-2 touch-device:mt-36">
          <div className="relative">
            <img
              className="mx-auto w-screen mobile:w-auto"
              src={
                import.meta.env.VITE_APP_ASSETS_BASE_URL +
                (tabletOrLargerMode
                  ? mode === "best-formation"
                    ? "/fc24/misc/best_formation_pitch_v3.svg"
                    : "/misc/pitch.svg"
                  : "/fc24/misc/mobile_pitch_v1.png")
              }
              alt={"pitch"}
            />
            <img
              src="https://fut-content.s3.eu-west-1.amazonaws.com/fc24/misc/easysbc-watermark.png"
              className="absolute w-[135px] desktop:w-[250px] bottom-[16%] left-4 desktop:bottom-[5%] desktop:left-[50px] touch-device:left-[205px]"
              alt={"www.easysbc.io"}
            />
          </div>
          <div className="absolute z-10 touch-device:z-20 top-12 desktop:top-4 touch-device:-top-12 w-[95%] desktop:w-[calc(80%)] touch-device:w-[calc(50%)] sb-v2-md:w-[calc(63%)] inset-x-0 flex items-center justify-center mobile:left-1/2 mobile:-translate-x-1/2 h-[80%] desktop:h-[80%] touch-device:h-[110%]">
            {pitchItems?.map((item, index) => (
              <div
                key={item.player?.resourceId || index}
                className={clsx(
                  " overflow-y-auto",
                  (item.tempPlayer || disablePlayerInteractions) &&
                    "pointer-events-none",
                )}
              >
                {item.player?.resourceId ? (
                  <Draggable
                    id={index.toString()}
                    left={`${item.x}%`}
                    top={`${item.y - 5.2}%`}
                    height={item.h}
                    width={item.w}
                  >
                    <Droppable id={index} key={index}>
                      <PlayerOnPitch
                        item={item}
                        squad={squad}
                        tempSquad={tempSquad}
                        mode={mode}
                        formationObject={formationObject}
                        changingPitchItemOnIndex={changingPitchItemOnIndex}
                        rightPanelMode={rightPanelMode}
                        playerRoles={playerRoles}
                        handleChangePlayerRole={handleChangePlayerRole}
                        handleRemovePlayerOrRole={handleRemovePlayerOrRole}
                        handleAddPlayer={handleAddPlayer}
                        handleAddPlayerFilters={handleAddPlayerFilters}
                        handleChangeChemstyle={handleChangeChemstyle}
                        handleAddEvoPlayer={handleAddEvoPlayer}
                        handleOnClickPlayer={handleOnClickPlayer}
                        handleOnClickPlayerMobile={handleOnClickPlayerMobile}
                        tabletOrLargerMode={tabletOrLargerMode}
                        desktopMode={desktopMode}
                      />
                    </Droppable>
                  </Draggable>
                ) : (
                  <DrawEmptyPosition
                    item={item}
                    isOverItem={parseInt(isOverItem)}
                    formationObject={formationObject}
                    handleAddPlayer={handleAddPlayer}
                    handleAddPlayerFilters={handleAddPlayerFilters}
                    handleChangePlayerRole={handleChangePlayerRole}
                    handleClearPlayerFilters={handleClearPlayerFilters}
                    changingPitchItemOnIndex={changingPitchItemOnIndex}
                    rightPanelMode={rightPanelMode}
                    tabletOrLargerMode={tabletOrLargerMode}
                    playerRoles={playerRoles}
                  />
                )}
              </div>
            ))}
          </div>
          {+activeId >= 0 && (
            <DragOverlay dropAnimation={null}>
              {+activeId <= 10 ? (
                <div
                  style={{
                    position: "absolute",
                    left: activeData?.left,
                    top: activeData?.top,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <div className="relative">
                    <PlayerOnPitch
                      item={pitchItems[+activeId]}
                      squad={squad}
                      tempSquad={tempSquad}
                      mode={mode}
                      formationObject={formationObject}
                      playerRoles={playerRoles}
                      tabletOrLargerMode={tabletOrLargerMode}
                      handleChangePlayerRole={handleChangePlayerRole}
                      handleRemovePlayerOrRole={handleRemovePlayerOrRole}
                      handleAddPlayer={handleAddPlayer}
                      handleAddPlayerFilters={handleAddPlayerFilters}
                      handleOnClickPlayer={handleOnClickPlayer}
                      handleOnClickPlayerMobile={handleOnClickPlayerMobile}
                      isDragging={true}
                    />
                  </div>
                </div>
              ) : (
                <DraggingPlayerOnBench
                  item={benchItems[+activeId - 11]}
                  mode={mode}
                  playerRoles={playerRoles}
                  tabletOrLargerMode={tabletOrLargerMode}
                  isDragging={true}
                />
              )}
            </DragOverlay>
          )}
        </div>
        <div className="w-full z-10 -mt-16 mobile:mt-0 touch-device:w-[calc(100%-520px)] sb-v2-lg:w-[calc(100%-704px)] flex flex-row pb-2 mobile:pb-0 px-1 mobile:px-0">
          <Bench
            benchItems={benchItems}
            tempBenchItems={tempBenchItems}
            isOverItem={parseInt(isOverItem)}
            handleAddPlayer={handleAddPlayer}
            handleChangePlayerRole={handleChangePlayerRole}
            handleOnClickPlayer={handleOnClickPlayer}
            handleOnClickPlayerMobile={handleOnClickPlayerMobile}
            changingPitchItemOnIndex={changingPitchItemOnIndex}
            rightPanelMode={rightPanelMode}
            tabletOrLargerMode={tabletOrLargerMode}
            handleRemovePlayer={handleRemovePlayerOrRole}
            desktopMode={desktopMode}
          />
        </div>
      </DndContext>
    </>
  );
};
